import { useCart } from "@/lib/cart";
import { useI18n, pickLocalized } from "@/lib/i18n";
import { formatEUR } from "@/lib/format";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2, Tag, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const CODE_LABELS = {
  de: { field: "Rabatt- oder Gutschein-Code", apply: "Anwenden", subtotal: "Zwischensumme", discount: "Rabatt", total: "Gesamt", remove: "Entfernen", invalid: "Code ungültig oder abgelaufen" },
  en: { field: "Discount or gift card code", apply: "Apply", subtotal: "Subtotal", discount: "Discount", total: "Total", remove: "Remove", invalid: "Code invalid or expired" },
  it: { field: "Codice sconto o carta regalo", apply: "Applica", subtotal: "Subtotale", discount: "Sconto", total: "Totale", remove: "Rimuovi", invalid: "Codice non valido o scaduto" },
  ja: { field: "割引・ギフトカードコード", apply: "適用", subtotal: "小計", discount: "割引", total: "合計", remove: "削除", invalid: "コードが無効または期限切れです" },
  zh: { field: "折扣码或礼品卡码", apply: "使用", subtotal: "小计", discount: "折扣", total: "合计", remove: "移除", invalid: "代码无效或已过期" },
};

export default function CartDrawer() {
  const { items, open, setOpen, remove, setQty, subtotal, count, discount, applyDiscount, clearDiscount, discountAmount, total } = useCart();
  const { t, lang } = useI18n();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const L = CODE_LABELS[lang] || CODE_LABELS.en;

  const submitCode = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    // Try discount first, then gift card
    try {
      const { data } = await api.post("/discount/validate", { code: code.trim(), subtotal_eur: subtotal });
      applyDiscount({ type: "discount", ...data, code_raw: code.trim() });
      setCode("");
      toast.success(`${data.code} · −${formatEUR(data.amount_off_eur, lang)}`);
      setBusy(false);
      return;
    } catch (_) {}
    try {
      const { data } = await api.post("/gift-card/validate", { code: code.trim() });
      const off = Math.min(data.remaining_eur, subtotal);
      applyDiscount({ type: "gift_card", code: data.code, code_raw: code.trim(), amount_off_eur: off, remaining_eur: data.remaining_eur });
      setCode("");
      toast.success(`${data.code} · −${formatEUR(off, lang)}`);
      setBusy(false);
      return;
    } catch (_) {}
    toast.error(L.invalid);
    setBusy(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60]"
            data-testid="cart-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 h-full w-full sm:w-[440px] bg-[#0A0A0A] border-l border-white/10 z-[70] flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-8 py-6 border-b border-white/10">
              <h3 className="font-display text-sm tracking-[0.3em] text-gold">
                {t.cart.t} <span className="text-cream/50">({count})</span>
              </h3>
              <button data-testid="cart-close" onClick={() => setOpen(false)} className="text-cream/60 hover:text-gold">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-8 py-6">
              {items.length === 0 ? (
                <p className="text-cream/50 font-body text-sm py-20 text-center" data-testid="cart-empty">{t.cart.empty}</p>
              ) : (
                <ul className="space-y-6">
                  {items.map(({ product, qty }) => (
                    <li key={product.id} className="flex gap-4 pb-6 border-b border-white/5" data-testid={`cart-item-${product.slug}`}>
                      <img src={product.image_url} alt="" className="w-20 h-24 object-cover clip-frame" />
                      <div className="flex-1 flex flex-col justify-between">
                        <div>
                          <div className="font-display text-[11px] tracking-[0.25em] text-cream">{pickLocalized(product, lang, "name")}</div>
                          <div className="text-xs text-cream/50 font-body mt-1">{product.weight_g} g ℮ · {formatEUR(product.price_eur, lang)}</div>
                        </div>
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center border border-white/10">
                            <button onClick={() => setQty(product.id, qty - 1)} className="p-1.5 text-cream/60 hover:text-gold" data-testid={`decrease-${product.slug}`}>
                              <Minus className="w-3 h-3" />
                            </button>
                            <span className="px-3 text-xs text-cream">{qty}</span>
                            <button onClick={() => setQty(product.id, qty + 1)} className="p-1.5 text-cream/60 hover:text-gold" data-testid={`increase-${product.slug}`}>
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                          <button onClick={() => remove(product.id)} className="text-cream/40 hover:text-gold" data-testid={`remove-${product.slug}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-white/10 px-8 py-6 space-y-4">
                {/* Discount / gift card code */}
                {!discount ? (
                  <form onSubmit={submitCode} className="flex gap-2" data-testid="cart-code-form">
                    <div className="flex-1 flex items-center gap-2 border border-white/15 px-3">
                      <Tag className="w-3.5 h-3.5 text-gold/70" />
                      <input
                        type="text"
                        value={code}
                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                        placeholder={L.field}
                        data-testid="cart-code-input"
                        className="bg-transparent outline-none text-cream text-xs tracking-widest py-2.5 w-full placeholder:text-cream/30"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={busy}
                      data-testid="cart-code-apply"
                      className="px-4 py-2.5 border border-gold/60 text-gold font-display text-[10px] tracking-[0.3em] hover:bg-gold hover:text-black transition-colors"
                    >
                      {L.apply}
                    </button>
                  </form>
                ) : (
                  <div className="flex items-center justify-between border border-gold/50 bg-gold/5 px-3 py-2.5" data-testid="cart-code-applied">
                    <div className="flex items-center gap-2 text-xs">
                      <Check className="w-3.5 h-3.5 text-gold" />
                      <span className="font-display tracking-[0.2em] text-gold text-[10px]">{discount.code}</span>
                      <span className="text-cream/50 font-body">−{formatEUR(discountAmount, lang)}</span>
                    </div>
                    <button
                      type="button"
                      onClick={clearDiscount}
                      data-testid="cart-code-remove"
                      className="text-cream/50 hover:text-gold text-[10px] font-display tracking-widest"
                    >
                      {L.remove}
                    </button>
                  </div>
                )}

                <div className="space-y-1 text-xs">
                  <div className="flex justify-between items-baseline text-cream/60">
                    <span className="font-display text-[10px] tracking-[0.3em]">{L.subtotal}</span>
                    <span className="font-editorial">{formatEUR(subtotal, lang)}</span>
                  </div>
                  {discount && (
                    <div className="flex justify-between items-baseline text-gold">
                      <span className="font-display text-[10px] tracking-[0.3em]">{L.discount}</span>
                      <span className="font-editorial">−{formatEUR(discountAmount, lang)}</span>
                    </div>
                  )}
                </div>
                <div className="flex justify-between items-baseline pt-1 border-t border-white/10 pt-3">
                  <span className="font-display text-[10px] tracking-[0.3em] text-cream/80">{L.total}</span>
                  <span className="font-editorial text-2xl text-gold" data-testid="cart-total">{formatEUR(total, lang)}</span>
                </div>
                <p className="text-[10px] text-cream/40 font-body">{t.cart.ship}</p>
                <Link
                  to="/checkout"
                  onClick={() => setOpen(false)}
                  data-testid="checkout-button"
                  className="block w-full text-center py-4 bg-gold text-black font-display text-[11px] tracking-[0.3em] hover:bg-gold-light transition-colors gold-shine"
                  style={{ background: "#C9A24C" }}
                >
                  {t.cart.checkout}
                </Link>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
